/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI Text Completion & LLM Streaming DTOs (OpenAPI 3.1 Compatible)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Single AI Message Schema in chat conversation.
 */
export const AiMessageSchema = z
  .object({
    role: z
      .enum(['system', 'user', 'assistant'])
      .openapi({
        description: 'Role of the message sender',
        example: 'user',
      }),
    content: z
      .string()
      .min(1, 'Message content cannot be empty')
      .openapi({
        description: 'Text content of the message',
        example: 'Write a professional product launch announcement for Sophia Platform 2.0.',
      }),
  })
  .openapi('AiMessage');

export type AiMessageDto = z.infer<typeof AiMessageSchema>;

/**
 * Text Completion Streaming Request Schema (POST /api/v1/text/completions/stream).
 */
export const TextCompletionStreamSchema = z
  .object({
    model: z
      .string()
      .min(1)
      .default('gpt-4o')
      .openapi({
        description: 'Target AI model identifier across supported providers',
        example: 'gpt-4o',
      }),
    messages: z
      .array(AiMessageSchema)
      .min(1, 'At least one message is required')
      .openapi({
        description: 'Ordered sequence of conversational messages',
      }),
    systemPrompt: z
      .string()
      .optional()
      .openapi({
        description: 'Optional system instruction prepended to conversation context',
        example: 'You are an expert SaaS marketing strategist and technical writer.',
      }),
    temperature: z
      .number()
      .min(0.0)
      .max(2.0)
      .default(0.7)
      .optional()
      .openapi({
        description: 'Sampling temperature between 0.0 (deterministic) and 2.0 (creative)',
        example: 0.7,
      }),
    maxTokens: z
      .number()
      .int()
      .min(1)
      .max(128000)
      .default(4096)
      .optional()
      .openapi({
        description: 'Maximum number of output tokens to generate',
        example: 4096,
      }),
    documentId: z
      .string()
      .uuid()
      .optional()
      .openapi({
        description: 'Optional document UUID to persist the generated completion content',
        example: '123e4567-e89b-12d3-a456-426614174000',
      }),
    title: z
      .string()
      .max(255)
      .optional()
      .openapi({
        description: 'Optional document title if persisting as a new workspace document',
        example: 'Sophia 2.0 Product Launch Announcement',
      }),
  })
  .openapi('TextCompletionStreamRequest');

export type TextCompletionStreamDto = z.infer<typeof TextCompletionStreamSchema>;

// --- SSE Event Data Schemas (For Documentation & Type-Safety) ---

/**
 * SSE 'start' event data payload.
 */
export const SseStartEventSchema = z
  .object({
    jobId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    model: z.string().openapi({ example: 'gpt-4o' }),
    provider: z.string().openapi({ example: 'openai' }),
    isByok: z.boolean().openapi({ example: false }),
    timestamp: z.number().openapi({ example: 1729340000000 }),
  })
  .openapi('SseStartEvent');

export type SseStartEventDto = z.infer<typeof SseStartEventSchema>;

/**
 * SSE 'delta' event data payload.
 */
export const SseDeltaEventSchema = z
  .object({
    text: z.string().optional().openapi({ example: 'We are thrilled to announce...' }),
    reasoning: z.string().optional().openapi({ example: 'Analyzing product positioning...' }),
  })
  .openapi('SseDeltaEvent');

export type SseDeltaEventDto = z.infer<typeof SseDeltaEventSchema>;

/**
 * SSE 'usage' event data payload.
 */
export const SseUsageEventSchema = z
  .object({
    inputTokens: z.number().int().openapi({ example: 45 }),
    outputTokens: z.number().int().openapi({ example: 320 }),
    totalTokens: z.number().int().openapi({ example: 365 }),
    costCredits: z.number().openapi({ example: 0.365 }),
    isByok: z.boolean().openapi({ example: false }),
    walletBalance: z.number().optional().openapi({ example: 4999.635 }),
  })
  .openapi('SseUsageEvent');

export type SseUsageEventDto = z.infer<typeof SseUsageEventSchema>;

/**
 * SSE 'done' event data payload.
 */
export const SseDoneEventSchema = z
  .object({
    jobId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    status: z.enum(['completed', 'aborted']).openapi({ example: 'completed' }),
    finishReason: z.string().optional().openapi({ example: 'stop' }),
    durationMs: z.number().openapi({ example: 1250 }),
  })
  .openapi('SseDoneEvent');

export type SseDoneEventDto = z.infer<typeof SseDoneEventSchema>;

/**
 * SSE 'error' event data payload.
 */
export const SseErrorEventSchema = z
  .object({
    code: z.string().openapi({ example: 'AI_PROVIDER_ERROR' }),
    message: z.string().openapi({ example: 'Upstream rate limit reached. Key quarantined.' }),
  })
  .openapi('SseErrorEvent');

export type SseErrorEventDto = z.infer<typeof SseErrorEventSchema>;
